import { useState } from "react";
import { Sparkles, Handshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteLayout } from "../components/site/SiteLayout";
import { AnnouncementBar } from "../components/site/AnnouncementBar";
import { PromoCarousel } from "../components/site/PromoCarousel";
import { TabPills, HOME_TABS } from "../components/site/TabPills";
import { HomeGameGrid } from "../components/site/GameGrid";
import { ProvidersCarousel } from "../components/site/ProvidersCarousel";
import { ExclusiveGames, HotSlots, HotCasino, NewGames } from "../components/site/ExclusiveGames";
import {
  TrustStrip,
  LiveWinsTicker,
} from "../components/site/HomeTrustSections";
import {
  SPORTS_TABS,
  CASINO_VENDORS,
  SLOT_VENDORS,
  CRASH_VENDORS,
  TABLE_VENDORS,
  FISHING_VENDORS,
  ARCADE_VENDORS,
  LOTTERY_VENDORS,
} from "../data/site";

const SOURCES = {
  SPORTS_TABS,
  CASINO_VENDORS,
  SLOT_VENDORS,
  CRASH_VENDORS,
  TABLE_VENDORS,
  FISHING_VENDORS,
  ARCADE_VENDORS,
  LOTTERY_VENDORS,
};

// Override the Popular tab so it uses baji.live's gold crown PNG (icon-exclusive.png).
// Also drop `to` on all tabs so they act as in-page filters on the home page.
const POPULAR_CROWN_ICON = "https://img.b112j.com/bj/h5/assets/v3/images/icon-set/menu-type/active/icon-exclusive.png";
const HOME_FILTER_TABS = HOME_TABS.map(({ to: _to, ...rest }) => {
  if (rest.id === "popular") {
    return { ...rest, icon: POPULAR_CROWN_ICON };
  }
  return rest;
});

export default function HomePage() {
  const [activeTab, setActiveTab] = useState("popular");

  return (
    <SiteLayout>
      <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 lg:pr-8">
        <div className="pt-3 sm:pt-5">
          <PromoCarousel />
        </div>
      </div>

      <AnnouncementBar />

      {/* Agent income invitation — animated Bangla banner (mobile-first) */}
      <Link
        to="/become-agent"
        data-testid="home-agent-income-banner"
        className="group block relative overflow-hidden"
        style={{ background: "linear-gradient(90deg, #052e1c 0%, #064e34 45%, #059669 100%)" }}
      >
        <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 lg:pr-8 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3">
          {/* Icon — hidden on very small phones */}
          <div className="shrink-0 hidden xs:flex sm:flex w-7 h-7 sm:w-8 sm:h-8 rounded-full items-center justify-center bg-emerald-400/20 border border-emerald-300/40">
            <Handshake size={14} className="text-emerald-200 group-hover:rotate-[-8deg] transition-transform" />
          </div>

          {/* Text block — wraps to 2 lines on mobile if needed */}
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2 sm:flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400 text-black text-[9px] sm:text-[10px] font-black px-1.5 sm:px-2 py-0.5 tracking-wide">
                <Sparkles size={9} className="sm:hidden" />
                <Sparkles size={10} className="hidden sm:inline" />
                AGENT
              </span>
              <span className="text-[12px] sm:text-[14px] lg:text-[15px] font-bold text-white leading-tight">
                Maxlive88 agent হয়ে&nbsp;
                <span
                  className="font-black text-amber-300"
                  style={{ textShadow: "0 0 12px rgba(251,191,36,0.55)" }}
                >
                  আজীবন ইনকাম
                </span>
                &nbsp;করার সুযোগ
              </span>
            </div>
            <span className="hidden md:inline text-[11px] text-emerald-100/85">
              ৬% deposit + ৪% withdraw commission · ১৫ দিনে auto payout
            </span>
          </div>

          {/* CTA — compact on mobile */}
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white/95 text-emerald-800 text-[10px] sm:text-[11px] font-black px-2.5 sm:px-3 py-1 group-hover:bg-white group-hover:-translate-x-0.5 transition-all">
            <span className="hidden sm:inline">আবেদন করুন</span>
            <span className="sm:hidden">যোগ দিন</span>
            <ArrowRight size={11} className="sm:hidden" />
            <ArrowRight size={12} className="hidden sm:inline" />
          </span>
        </div>
        {/* Subtle animated shimmer strip */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-full w-1/3 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/15 to-transparent"
          style={{ animation: "bap-shine 6s ease-in-out infinite" }}
        />
        <style>{`
          @keyframes bap-shine {
            0%, 60% { transform: translateX(0); }
            80%     { transform: translateX(400%); }
            100%    { transform: translateX(400%); }
          }
        `}</style>
      </Link>

      <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 lg:pr-8">
        {/* Trust USPs — bonus / withdrawal / support / license */}
        <TrustStrip />

        {/* Live wins marquee — social proof */}
        <LiveWinsTicker />

        {/* Tab pills act as in-page category filters */}
        <TabPills tabs={HOME_FILTER_TABS} active={activeTab} onChange={setActiveTab} />

        <div className="pb-2">
          <HomeGameGrid activeTab={activeTab} sources={SOURCES} />
        </div>

        <ProvidersCarousel />

        <ExclusiveGames />
        <HotSlots />
        <HotCasino />
        <NewGames />

        {/* StatsCounter, WhyChooseUs, LicenseBadges removed per request */}

        <section className="py-3 sm:py-6 border-t border-white/5 mt-2 sm:mt-4">
          <div className="flex items-center gap-2.5 sm:gap-3 mb-2">
            <Sparkles className="text-[#ffc933]" size={20} />
            <h3 className="text-lg sm:text-2xl font-bold text-white">
              Play Smart, Win <span className="text-[#ffc933]">Big</span>
            </h3>
          </div>
          <p className="text-white/60 text-xs sm:text-sm leading-relaxed max-w-3xl">
            Maxlive88 is a leading online gaming and betting platform, offering an extensive
            range of sports books, live casino, slots, crash, table, fishing, arcade and
            lottery games from world-class providers including Evolution, Pragmatic Play,
            JILI, Microgaming, Spribe and many more. Backed by a verifiable Anjouan gaming
            license, instant local payments and 24/7 Bangla-speaking support — join thousands
            of players who trust Maxlive88 with their play.
          </p>
        </section>
      </div>
    </SiteLayout>
  );
}
